import * as React from 'react'

import { styled } from '~/styled'

import { ActionIcon } from '../action-icon/ActionIcon'
import { Icon } from '../icon/Icon'
import { Tooltip } from '../tooltip/Tooltip'

const StyledStepperButton = styled(ActionIcon, {
  base: [
    'rounded-md',
    'z-1',
    'h-full!',
    'hover:[&_svg]:text-grey-800',
    'hover:bg-grey-100',
    '[&_svg]:text-grey-700',
    'active:[&_svg]:text-grey-900',
    'active:bg-grey-200',
    'disabled:opacity-30',
    'disabled:pointer-events-none'
  ],
  variants: {
    fieldAppearance: {
      standard: ['bg-white', 'border-grey-800!'],
      modern: ['bg-grey-100', 'border-grey-100!', 'hover:bg-grey-200!']
    },
    fieldTheme: {
      white: [],
      grey: []
    },
    emphasis: {
      bold: [
        '[&_svg]:text-primary-700',
        'hover:bg-primary-100!',
        'hover:[&_svg]:text-primary-800!',
        'active:bg-primary-200!',
        'active:[&_svg]:text-primary-900!'
      ]
    }
  },
  compoundVariants: [
    {
      fieldAppearance: 'modern',
      fieldTheme: 'white',
      class: ['bg-white']
    },
    {
      fieldAppearance: 'standard',
      emphasis: 'bold',
      class: ['bg-white', 'border-primary-800!']
    },
    {
      fieldAppearance: 'modern',
      emphasis: 'bold',
      class: ['bg-white!', 'hover:bg-primary-100!']
    }
  ]
})

type NumberInputStepperProps = Omit<
  React.ComponentProps<typeof ActionIcon>,
  'children'
> &
  Omit<React.ComponentProps<typeof StyledStepperButton>, 'children'> & {
    icon: React.FC<React.SVGProps<SVGSVGElement>>
    showTooltip?: boolean
    disabledTooltipContent?: string
  }

export const NumberInputStepper = React.forwardRef<
  HTMLButtonElement,
  NumberInputStepperProps
>((props, forwardedRef) => {
  const {
    icon,
    disabledTooltipContent,
    showTooltip,
    fieldAppearance = 'standard',
    fieldTheme,
    ...rest
  } = props

  /**
   * Focus has been removed from the button
   * as the increment and decrement buttons should be keyboard accessible via arrow keys.
   * see MDN docs https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/spinbutton_role
   */
  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <span className="z-1" tabIndex={-1}>
          <StyledStepperButton
            hasTooltip={false}
            tabIndex={-1}
            appearance="outline"
            fieldAppearance={fieldAppearance}
            fieldTheme={fieldTheme}
            ref={forwardedRef}
            {...rest}
          >
            <Icon is={icon} />
          </StyledStepperButton>
        </span>
      </Tooltip.Trigger>
      {showTooltip && (
        <Tooltip.Content>{disabledTooltipContent}</Tooltip.Content>
      )}
    </Tooltip>
  )
})
